import { eqProps, flatten, uniqWith } from 'ramda';

import {
  lockRoute,
  setScheduleRequired,
  updateStop,
  setRequests,
  setStops,
  setSaveRouteEnable,
} from '../reducers/route';
import {
  getRouteDetail,
  isRouteStatusLoading,
  getCurrentRouteId,
  getRouteRequests,
  getRouteStops,
  getOriginalParcelsTransferData,
  getRequestOrder,
  isRouteReadyOrOpen,
} from '../selectors/route';
import {
  getStopByNumber,
  updateStopPostcodeData,
  updateStopTime,
  validatePostcode,
  validateStopTime,
} from '../../features/routeDetail/model';
import * as postcodeService from '../../services/postcode';
import * as stopService from '../../services/stop';
import { DISMISS, INVALID_POSTCODE } from '../../constants/strings';
import { formatSecondsToMMSS } from '../../utils/date';
import { getSelectedDepot } from '../selectors/app';
import { generateMoveParcelRequests } from '../../models/request';
import {
  getSelectedPreparedStops,
  getSelectedStops,
} from '../selectors/ui/routeDetail';
import { getMergeToParcelList } from '../../models/stop';
import { showNotification } from './notifications';
import { NotificationType } from '../../features/Notifier';
import {
  getSortedStops,
  replaceMergeTargetAndRemoveSelected,
  updateSeqAndDisplayNumbers,
} from '../../models/moveStops';
import { updateSelectedStops } from '../reducers/ui/routeDetail/stopFilters';
import { getErrorMessage } from '../../utils/errors';
import { removeWhiteSpace } from '../../utils/string';
import { getDowngradesProps } from '../selectors/schedule';

export const handleStopTimeChange =
  (inputValue, { setFieldError, resetForm }, { stopTime, stopNumber }) =>
  async (dispatch, getState) => {
    if (inputValue === formatSecondsToMMSS(stopTime)) {
      resetForm();
      return;
    }
    const currentUserLock = await dispatch(lockRoute());

    const state = getState();

    if (!currentUserLock) {
      resetForm();
      return;
    }

    const validationError = validateStopTime(inputValue);

    if (validationError) {
      setFieldError(validationError);
      return;
    }

    const routeDetail = getRouteDetail(state);
    const stop = getStopByNumber(routeDetail.stop, stopNumber);

    const updatedStop = updateStopTime(stop, inputValue);

    dispatch(updateStop(updatedStop));
    dispatch(setScheduleRequired(true));
    dispatch(setSaveRouteEnable(isRouteReadyOrOpen(state)));
  };

export const handleStopPostcodeChange =
  (inputValue, { setFieldError, resetForm }, { postcode, stopNumber }) =>
  async (dispatch, getState) => {
    if (inputValue === postcode) {
      resetForm();
      return;
    }

    const currentUserLock = await dispatch(lockRoute());

    const state = getState();

    if (!currentUserLock) {
      resetForm();
      return;
    }

    try {
      const postcode = removeWhiteSpace(inputValue);
      const validationError = validatePostcode(postcode);

      if (validationError) {
        setFieldError(INVALID_POSTCODE);
        return;
      }

      const postcodeData = await postcodeService.getPostcodeLocation(postcode);
      const routeDetail = getRouteDetail(state);
      const stop = getStopByNumber(routeDetail.stop, stopNumber);

      const updatedStop = updateStopPostcodeData(stop, postcodeData);

      dispatch(updateStop(updatedStop));
      dispatch(setScheduleRequired(true));
      dispatch(setSaveRouteEnable(isRouteReadyOrOpen(state)));
    } catch {
      setFieldError(INVALID_POSTCODE);
    }
  };

export const updateKingParcel =
  ({ rowData }) =>
  async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());

    if (!currentUserLock) {
      return;
    }

    const state = getState();
    const routeDetail = getRouteDetail(state);
    const { stopNumber } = getSelectedStops(state)[0];
    const stop = getStopByNumber(routeDetail.stop, stopNumber);
    const parcel = stop.parcel.slice();
    const kingParcelIndex = parcel.findIndex(
      ({ parcelIndex }) => parcelIndex === 1
    );
    const selectedParcelIndex = parcel.findIndex(
      ({ parcelCode }) => parcelCode === rowData.parcelCode
    );

    if (kingParcelIndex > -1) {
      parcel[kingParcelIndex] = {
        ...parcel[kingParcelIndex],
        parcelIndex: parcel[selectedParcelIndex].parcelIndex,
      };
    }

    parcel[selectedParcelIndex] = {
      ...parcel[selectedParcelIndex],
      parcelIndex: 1,
    };

    dispatch(updateStop({ ...stop, parcel }));
    dispatch(setScheduleRequired(true));
  };

export const onChangeBulkStop = () => async (dispatch, getState) => {
  const currentUserLock = await dispatch(lockRoute());

  const state = getState();
  const { stopNumber } = getSelectedPreparedStops(state)[0];

  if (!currentUserLock) {
    return;
  }

  const routeDetail = getRouteDetail(state);
  const stop = getStopByNumber(routeDetail.stop, stopNumber);

  dispatch(
    updateStop({
      ...stop,
      bulk: !stop.bulk,
    })
  );
  dispatch(setScheduleRequired(true));
};

export const validateStopMerge =
  ({
    parcels,
    checkOpenRequests,
    shopUnsuitable = false,
    splitStop = false,
    parcelValidated = true,
  }) =>
  (dispatch, getState) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const routeDetail = getRouteDetail(state);
    const { routePlannerDowngrades = false } = getDowngradesProps(state);
    // remove duplicate parcelCodes for cases like stop transfer
    const parcelCodeEq = eqProps('parcelCode');
    const filteredParcels = uniqWith(parcelCodeEq)(parcels);

    return stopService.validateStopMerge(
      depotCode,
      routeDetail.routeId,
      filteredParcels,
      checkOpenRequests,
      shopUnsuitable,
      splitStop,
      routePlannerDowngrades,
      parcelValidated
    );
  };

export const createMoveParcelsRequests =
  (parcelList, fromStop, toStop) => (dispatch, getState) => {
    const state = getState();
    const routeId = getCurrentRouteId(state);
    const originalParcels = getOriginalParcelsTransferData(state);
    const requestOrder = getRequestOrder(state);

    const newRequests = generateMoveParcelRequests(
      parcelList,
      fromStop,
      toStop,
      routeId,
      originalParcels,
      requestOrder
    );
    const requests = getRouteRequests(state);

    dispatch(setRequests(requests.concat(newRequests)));
  };

export const onMergeStopPick = (pickedStop) => async (dispatch, getState) => {
  const selectedStops = getSelectedStops(getState());
  const parcels = getMergeToParcelList(selectedStops, pickedStop);

  try {
    return await dispatch(
      validateStopMerge({ parcels, checkOpenRequests: true })
    );
  } catch (error) {
    dispatch(
      showNotification({
        key: 'merge-stop-message',
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
      })
    );
    return null;
  }
};

export const mergeStopsByDeliveryId = (stopsToMerge) => async (dispatch) => {
  const currentUserLock = await dispatch(lockRoute());

  if (!currentUserLock) {
    return;
  }

  const mergedStops = [];
  const failureStops = [];
  const stopIdsToRemove = new Set();

  for (const [deliveryId, stops] of Object.entries(stopsToMerge)) {
    const parcels = getMergeToParcelList(stops.slice(1), stops[0]);
    try {
      const newStop = await dispatch(
        validateStopMerge({ parcels, checkOpenRequests: true })
      );
      mergedStops.push({ ...stops[0], ...newStop, reconsolidated: true });
      stops.slice(1).forEach((stop) => stopIdsToRemove.add(stop.stopId));
    } catch (error) {
      failureStops.push(
        `Unable to merge stops for Delivery ID ${deliveryId}. Reason: ${getErrorMessage(error)}`
      );
    }
  }

  return [mergedStops, failureStops, stopIdsToRemove];
};

export const onReconsolidateStops =
  (stopsToReconsolidation) => async (dispatch, getState) => {
    const state = getState();
    const stops = getRouteStops(state);
    const [mergedStops, failureStops, stopIdsToRemove] = await dispatch(
      mergeStopsByDeliveryId(stopsToReconsolidation)
    );

    if (mergedStops.length > 0) {
      const mergedStopIds = new Set(mergedStops.map(({ stopId }) => stopId));
      const replacedStopList = stops.reduce((acc, stop) => {
        if (mergedStopIds.has(stop.stopId)) {
          acc.push(mergedStops.find(({ stopId }) => stopId === stop.stopId));
        } else if (!stopIdsToRemove.has(stop.stopId)) {
          acc.push(stop);
        }
        return acc;
      }, []);

      dispatch(setStops(updateSeqAndDisplayNumbers(replacedStopList, true)));
      dispatch(setScheduleRequired(true));
      dispatch(setSaveRouteEnable(false));
    }

    if (failureStops.length > 0) {
      failureStops.map((message) =>
        dispatch(
          showNotification({
            key: `reconsolidation-stop-message-${message}`,
            type: NotificationType.ACTION,
            message,
            buttonText: DISMISS,
          })
        )
      );
    }

    return mergedStops;
  };

export const mergeStops =
  (newStop, destinationStopNumber) => async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());

    if (!currentUserLock) {
      return;
    }

    const state = getState();

    const isLoading = isRouteStatusLoading(state);
    const stops = getRouteStops(state);
    const selectedStops = getSelectedStops(state);
    const { routeId, request: requests } = getRouteDetail(state);
    const originalParcels = getOriginalParcelsTransferData(state);
    const requestOrder = getRequestOrder(state);

    dispatch(updateSelectedStops([newStop.stopId]));
    if (isLoading) {
      const newRequests = flatten(
        selectedStops.reduce(
          (acc, stop) =>
            stop.stopNumber === destinationStopNumber
              ? acc
              : [
                  ...acc,
                  generateMoveParcelRequests(
                    stop.parcel,
                    stop,
                    newStop,
                    routeId,
                    originalParcels,
                    requestOrder
                  ),
                ],
          []
        )
      );
      dispatch(setRequests(requests.concat(newRequests)));
    }

    const sortedStops = getSortedStops(stops);
    const replacedStopList = replaceMergeTargetAndRemoveSelected(
      sortedStops,
      selectedStops,
      newStop
    );

    const updatedStopList = updateSeqAndDisplayNumbers(replacedStopList, true);

    dispatch(setStops(updatedStopList));
    dispatch(setScheduleRequired(true));
    dispatch(setSaveRouteEnable(false));
  };

export const removeStop =
  ({ stopNumber }) =>
  (dispatch, getState) => {
    const stops = getRouteStops(getState());
    const updatedStopList = stops.filter(
      (stop) => stop.stopNumber !== stopNumber
    );
    dispatch(setStops(updatedStopList));
  };

export const processRequestStop = (stop, requests) => (dispatch) => {
  const newParcels = stop?.parcel.filter(
    ({ parcelCode }) =>
      !requests.find((request) => request.parcelCode === parcelCode)
  );

  if (newParcels.length) {
    dispatch(updateStop({ ...stop, parcel: newParcels }));
  } else {
    dispatch(removeStop(stop));
  }
};
